import { JobState, OrderState, SiteType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { uniqBy } from 'lodash-es';
import { computed, makeObservable, observable, runInAction } from 'mobx';

import { ACTIVE_JOB_STATES } from '~components/Maps/v2/constants';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { Pagination } from '~services/pagination';
import {
  persistDateRange,
  retrievePersistedDateRange,
} from '~utils/dateRangePersistence';

dayjs.extend(utc);
dayjs.extend(timezone);

const defaultPaginationState = {
  limit: 10,
  page: 1,
};

export type OrderJobsFilters = {
  jobStates?: string[];
};

export type Filters = {
  customerAccountIds?: string[];
  departmentIds?: string[];
  dispatchNumberIds?: string[];
  driverIds?: string[];
  dropoffSites?: string[];
  endDate?: string;
  jobStates?: JobState[];
  orderStates?: OrderState[];
  pickUpSites?: string[];
  projectIds?: string[];
  projectsExternalIds?: string[];
  requesterIds?: string[];
  search?: string;
  siteTypes?: SiteType[];
  startDate?: string;
  vendorAccountIds?: string[];
};

export enum LiveMapStorageKeys {
  // The user's chosen date range, persisted across navigation and reloads.
  // Explicit nulls mean "All dates".
  // Replaces the dead `liveMap_appliedFilters` key, which was read but never
  // written anywhere.
  APPLIED_DATE_FILTERS = 'liveMap_appliedDateFilters',
}

// A function rather than a constant so the today default reflects the day the
// reset happens, not the day the module was loaded.
const getDefaultFilters = () => ({
  jobStates: ACTIVE_JOB_STATES,
  search: '',
  startDate: dayjs.tz().format('YYYY-MM-DD'),
  endDate: dayjs.tz().format('YYYY-MM-DD'),
});

class LiveMapStoreNew {
  filters: Filters = getDefaultFilters();
  hasMore: boolean = false;
  hasUnfilteredResults: boolean = false;
  isLoadingOrders: boolean = false;
  isLoadingOrderJobs: Map<string, boolean> = new Map();
  isSavingOrder: boolean = false;
  isSidebarCollapsed: boolean = false;
  orders: Order[] = [];
  orderJobs: Map<string, Job[]> = new Map();
  ordersPendingToShown: Order[] = [];
  pagination: Pagination = { ...defaultPaginationState };

  constructor() {
    makeObservable(this, {
      filters: observable,
      hasMore: observable,
      hasUnfilteredResults: observable,
      isLoadingOrders: observable,
      isLoadingOrderJobs: observable,
      isSidebarCollapsed: observable,
      orders: observable,
      orderJobs: observable,
      ordersPendingToShown: observable,
      pagination: observable,
      hasActiveNonSearchFilters: computed,
    });
  }

  // `search` never counts; `jobStates` (the In Progress/History tab) and
  // `orderStates` are identity — both survive clearing, so neither counts.
  // Dates default to today, so this is usually true — intended: clearing from
  // the hint also resets the date narrowing (TRE-15529).
  get hasActiveNonSearchFilters() {
    const {
      search: _search,
      jobStates: _jobStates,
      orderStates: _orderStates,
      ...rest
    } = this.filters;

    return Object.values(rest).some(
      (value) =>
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0),
    );
  }

  setHasUnfilteredResults(hasUnfilteredResults: boolean) {
    runInAction(() => {
      this.hasUnfilteredResults = hasUnfilteredResults;
    });
  }

  clearAllFiltersExceptSearch() {
    runInAction(() => {
      const { search, jobStates, orderStates } = this.filters;

      // The user chose "All dates" via the clear-filters hint: persist explicit
      // nulls so returning to the page does not re-default to today (TRE-15856).
      persistDateRange(LiveMapStorageKeys.APPLIED_DATE_FILTERS, {
        endDate: null,
        startDate: null,
      });

      this.resetOrders({ search, jobStates, orderStates });
    });
  }

  resetFiltersForCompanyChange() {
    // A company switch invalidates the previous company's persisted date range.
    localStorage.removeItem(LiveMapStorageKeys.APPLIED_DATE_FILTERS);

    const { jobStates } = this.filters;

    // jobStates (the In Progress/History tab identity) always survives the
    // switch. The in-memory range resets to the today default alongside the
    // persisted copy above, so the session and a later reload agree (TRE-15856).
    this.resetOrders({ ...getDefaultFilters(), jobStates });
  }

  ordersFetchStart() {
    runInAction(() => {
      this.isLoadingOrders = true;
    });
  }

  ordersFetchEnd(orders: Order[], pagination?: Pagination) {
    runInAction(() => {
      this.isLoadingOrders = false;
      if (!pagination || (!pagination.after && !pagination.before)) {
        this.orders = orders;
      } else {
        this.orders = uniqBy(this.orders.concat(orders), (order) => order.id);
      }
      this.pagination = {
        limit: defaultPaginationState.limit,
        after: pagination?.after ?? '',
        page: defaultPaginationState.page,
      };
      this.hasMore = Boolean(pagination?.after);
    });
  }

  orderJobsFetchStart(orderId: string) {
    runInAction(() => {
      this.isLoadingOrderJobs.set(orderId, true);
    });
  }

  orderJobsFetchEnd(orderId: string, jobs: Job[]) {
    runInAction(() => {
      this.isLoadingOrderJobs.set(orderId, false);
      this.orderJobs.set(orderId, jobs);
    });
  }

  orderUpdateEnd(order?: Order) {
    runInAction(() => {
      this.isSavingOrder = false;

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  orderMatchesCurrentFilteringCriteria(order: Order) {
    if (this.filters.orderStates?.length) {
      return Boolean(this.filters.orderStates.includes(order.state));
    }

    return true;
  }

  resetOrders(filters: Filters = {}) {
    runInAction(() => {
      this.filters = filters;
      this.hasMore = false;
      this.hasUnfilteredResults = false;
      this.isLoadingOrderJobs = new Map();
      this.isLoadingOrders = false;
      this.orderJobs = new Map();
      this.orders = [];
      this.pagination = { ...defaultPaginationState };
    });
  }

  setFilters(filters: Filters, merge?: boolean) {
    this.resetOrders(merge ? { ...this.filters, ...filters } : filters);
  }

  upsertOrder(order: Order) {
    runInAction(() => {
      const index = this.orders.findIndex(({ id }) => id === order.id);

      if (index > -1) {
        this.orders.splice(index, 1, order);
      } else {
        const isMatchingFilters = this.orderMatchesCurrentFilteringCriteria(order);

        if (isMatchingFilters) {
          this.ordersPendingToShown = uniqBy(
            this.ordersPendingToShown.concat([order]),
            (order) => order.id,
          );
        }
      }
    });
  }

  upsertJob(job: Job) {
    if (!job.order?.id) {
      return;
    }

    const orderJobs = this.orderJobs.get(job.order.id);

    if (orderJobs) {
      const index = orderJobs.findIndex(({ id }) => id === job.id);

      runInAction(() => {
        if (index > -1) {
          orderJobs.splice(index, 1, job);
        } else {
          orderJobs.push(job);
        }
      });
    }
  }

  restoreDateFilters() {
    const storedDateRange = retrievePersistedDateRange(
      LiveMapStorageKeys.APPLIED_DATE_FILTERS,
    );

    if (!storedDateRange) {
      return;
    }

    runInAction(() => {
      this.filters = {
        ...this.filters,
        endDate: storedDateRange.endDate ?? undefined,
        startDate: storedDateRange.startDate ?? undefined,
      };
    });
  }

  toggleSidebar() {
    runInAction(() => {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    });
  }
}

export default LiveMapStoreNew;
